/**
 * RISKEE Constants
 * Shared configuration for roles, report types, statuses, etc.
 */

export const ROLES = [
  { id: 'REPORTER', label: 'Staff / Reporter', description: 'Report hazards and view own submissions' },
  { id: 'SUPERVISOR', label: 'Supervisor', description: 'Triage, assign actions, verify' },
  { id: 'RISK_TEAM', label: 'Risk Team', description: 'Full risk oversight and governance' },
  { id: 'ORG_ADMIN', label: 'Org Admin', description: 'System configuration and user management' },
];

export const REPORT_TYPES = [
  { id: 'HAZARD', label: 'Hazard', icon: '/icons/hazard.png', color: '#f59e0b', description: 'Physical hazard or unsafe condition' },
  { id: 'NEAR_MISS', label: 'Near Miss', icon: '/icons/warning.png', color: '#8b5cf6', description: 'Close call without injury or damage' },
  { id: 'INCIDENT', label: 'Incident', icon: '/icons/danger.png', color: '#ef4444', description: 'Event causing injury or damage' },
  { id: 'RISK', label: 'Risk', icon: '/icons/risk_assessment.png', color: '#3b82f6', description: 'Potential future risk requiring assessment' },
  { id: 'IMPROVEMENT', label: 'Improvement', icon: null, emoji: '💡', color: '#10b981', description: 'Suggestion to improve safety' },
  { id: 'PSYCHOSOCIAL', label: 'Psychosocial', icon: '/icons/staff_welfare.png', color: '#722F37', description: 'Workplace wellbeing concern (restricted)' },
  { id: 'WELFARE', label: 'Welfare', icon: '/icons/staff_welfare.png', color: '#ec4899', description: 'Employee welfare concern (restricted)' },
];

export const DG_CLASSES = {
  '1': { label: 'Class 1 - Explosives', icon: '/icons/danger.png', color: '#ef4444' },
  '2.1': { label: 'Class 2.1 - Flammable Gas', icon: '/icons/flammable.png', color: '#ef4444' },
  '2.2': { label: 'Class 2.2 - Non-flammable Gas', icon: '/icons/warning.png', color: '#10b981' },
  '2.3': { label: 'Class 2.3 - Toxic Gas', icon: '/icons/hazard.png', color: '#8b5cf6' },
  '3': { label: 'Class 3 - Flammable Liquid', icon: '/icons/flammable.png', color: '#f59e0b' },
  '4.1': { label: 'Class 4.1 - Flammable Solid', icon: '/icons/flammable.png', color: '#ef4444' },
  '4.2': { label: 'Class 4.2 - Spontaneously Combustible', icon: '/icons/fire.png', color: '#ef4444' },
  '4.3': { label: 'Class 4.3 - Dangerous When Wet', icon: '/icons/warning.png', color: '#3b82f6' },
  '5.1': { label: 'Class 5.1 - Oxidizing', icon: '/icons/hazard.png', color: '#f59e0b' },
  '5.2': { label: 'Class 5.2 - Organic Peroxide', icon: '/icons/hazard.png', color: '#f97316' },
  '6.1': { label: 'Class 6.1 - Toxic', icon: '/icons/hazard.png', color: '#8b5cf6' },
  '6.2': { label: 'Class 6.2 - Infectious', icon: '/icons/hazard.png', color: '#ec4899' },
  '7': { label: 'Class 7 - Radioactive', icon: '/icons/warning.png', color: '#eab308' },
  '8': { label: 'Class 8 - Corrosive', icon: '/icons/hazard.png', color: '#f97316' },
  '9': { label: 'Class 9 - Miscellaneous', icon: '/icons/warning.png', color: '#6b7280' },
};

export const FIRE_SAFETY_TYPES = {
  'EXTINGUISHER': { label: 'Fire Extinguisher', icon: '/icons/extinguisher.png' },
  'HOSE_REEL': { label: 'Hose Reel', icon: '/icons/hose_reel.png' },
  'HYDRANT': { label: 'Hydrant', icon: '/icons/fire_hose.png' },
  'FIRE_DOOR': { label: 'Fire Door', icon: '/icons/exit.png' },
  'EXIT_SIGN': { label: 'Exit Sign', icon: '/icons/exit.png' },
  'EMERGENCY_LIGHT': { label: 'Emergency Light', icon: '/icons/warning.png' },
  'SMOKE_DETECTOR': { label: 'Smoke Detector', icon: '/icons/fire_safety.png' },
  'SPRINKLER': { label: 'Sprinkler', icon: '/icons/fire_safety.png' },
};

export const EQUIPMENT_CATEGORIES = {
  'PPE': { label: 'PPE', icon: '/icons/hardhat.png' },
  'FIRE_SAFETY': { label: 'Fire Safety', icon: '/icons/extinguisher.png' },
  'FIRST_AID': { label: 'First Aid', icon: '/icons/first_aid.png' },
  'TOOLS': { label: 'Tools', icon: null, emoji: '🔧' },
  'VEHICLES': { label: 'Vehicles', icon: null, emoji: '🚗' },
  'PLANT': { label: 'Plant & Machinery', icon: null, emoji: '🏗️' },
  'OTHER': { label: 'Other', icon: null, emoji: '📦' },
};

export const INCIDENT_CATEGORIES = {
  'INJURY': { label: 'Injury', icon: '/icons/first_aid.png', color: '#ef4444' },
  'FIRE': { label: 'Fire', icon: '/icons/fire.png', color: '#f97316' },
  'DG_SPILL': { label: 'DG Spill', icon: '/icons/flammable.png', color: '#8b5cf6' },
  'SECURITY': { label: 'Security', icon: '/icons/security_risks.png', color: '#3b82f6' },
  'NEAR_MISS': { label: 'Near Miss', icon: '/icons/warning.png', color: '#f59e0b' },
  'ENVIRONMENTAL': { label: 'Environmental', icon: null, emoji: '🌿', color: '#10b981' },
  'OTHER': { label: 'Other', icon: '/icons/hazard.png', color: '#6b7280' },
};

export const STATUS_CONFIG = {
  NEW: { label: 'New', color: '#3b82f6', bg: '#eff6ff' },
  TRIAGED: { label: 'Triaged', color: '#8b5cf6', bg: '#f5f3ff' },
  IN_TREATMENT: { label: 'In Treatment', color: '#f59e0b', bg: '#fffbeb' },
  VERIFICATION: { label: 'Verification', color: '#06b6d4', bg: '#ecfeff' },
  CLOSED: { label: 'Closed', color: '#10b981', bg: '#ecfdf5' },
  ARCHIVED: { label: 'Archived', color: '#6b7280', bg: '#f3f4f6' },
};

export const SEVERITY_CONFIG = {
  LOW: { label: 'Low', color: '#10b981', bg: '#ecfdf5' },
  MEDIUM: { label: 'Medium', color: '#f59e0b', bg: '#fffbeb' },
  HIGH: { label: 'High', color: '#f97316', bg: '#fff7ed' },
  CRITICAL: { label: 'Critical', color: '#ef4444', bg: '#fef2f2' },
};

export const ACTION_STATUS_CONFIG = {
  OPEN: { label: 'Open', color: '#3b82f6' },
  IN_PROGRESS: { label: 'In Progress', color: '#f59e0b' },
  BLOCKED: { label: 'Blocked', color: '#ef4444' },
  DONE: { label: 'Done', color: '#06b6d4' },
  VERIFIED: { label: 'Verified', color: '#10b981' },
  CANCELLED: { label: 'Cancelled', color: '#6b7280' },
};
